/**
 * Global admin auth interceptor.
 *
 * SECURITY (2026-09-25): admin access/refresh tokens now live in httpOnly
 * cookies (matching the earlier customer migration). This interceptor
 * refreshes on any 401 from an admin-facing URL by calling
 * POST /api/admin/auth/refresh (cookie-only, no body) and retrying once.
 *
 * NOTE: cookies are shared across every browser tab (unlike the old
 * sessionStorage tokens, which were tab-isolated) — logging in on one
 * admin tab now logs in every open admin tab, and a refresh failure here
 * logs all of them out.
 *
 * Call `installAdminAuthInterceptor()` exactly once at app startup from
 * App.js, BEFORE installCustomerAuthInterceptor so admin gets first look.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { AdminStorage } from '../utils/storage';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_URL = `${API}/api/admin/auth/refresh`;

// Dedup concurrent refresh attempts so parallel 401s trigger one refresh.
let refreshPromise = null;

const redirectToAdminLogin = () => {
  if (window.location.pathname.startsWith('/admin/login')) return;
  toast.error('Your admin session expired. Please log in again.');
  window.location.href = '/admin/login';
};

const runRefresh = async () => {
  const res = await axios.post(REFRESH_URL, {}, { withCredentials: true, _skipAuthRefresh: true });
  return res.data;
};

export const installAdminAuthInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      if (
        !response ||
        response.status !== 401 ||
        !config ||
        config._adminRetried ||
        config._skipAuthRefresh
      ) {
        return Promise.reject(error);
      }

      if (config.url && config.url.includes('/admin/auth/refresh')) {
        return Promise.reject(error);
      }

      // This interceptor only owns admin-facing calls.
      const url = config.url || '';
      if (!url.includes('/admin/')) {
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = runRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;

        config._adminRetried = true;
        config.withCredentials = true;
        return axios(config);
      } catch (refreshErr) {
        AdminStorage.clear();
        redirectToAdminLogin();
        return Promise.reject(error);
      }
    }
  );
};

export default installAdminAuthInterceptor;
